import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, userApi } from '../services/api';
import { clearAccessToken, getAccessToken, setAccessToken } from '../services/authStorage';
import { revokeChoreographyAuthorization } from '../services/choreographyAuth';
import { disableWebPush } from '../services/pushApi';
import { purgeRehearsalSnapshots } from '../components/choreography/choreographyCache';
import { purgeCollaborationQueues } from '../components/choreography/collaborationQueue';
import { clearAllDrafts } from '../utils/dialogDraftStore';
import {
  SESSION_BOOTSTRAP_MAX_ATTEMPTS,
  isSessionRejected,
  isTransientSessionFailure,
  sessionBootstrapBackoffMs,
  sessionUnavailableMessage,
} from './sessionBootstrap';

const AuthContext = createContext(null);

const PENDING_APPROVAL_CODES = new Set([
  'ACCOUNT_PENDING',
  'ACCOUNT_PENDING_APPROVAL',
  'PENDING_APPROVAL',
  'USER_PENDING_APPROVAL',
  'OAUTH_ACCOUNT_PENDING_APPROVAL',
]);

const isPendingApprovalPayload = (payload = {}) => {
  const user = payload.user || {};
  const code = payload.code || payload.errorCode || payload.error?.code || '';
  const status = payload.status || payload.accountStatus || payload.approvalStatus || user.status || '';

  return Boolean(payload.pendingApproval)
    || PENDING_APPROVAL_CODES.has(String(code))
    || String(status).toLowerCase() === 'pending'
    || String(status).toLowerCase() === 'pending_approval';
};

const getPendingApprovalMessage = (payload = {}) => {
  return payload.message
    || payload.error?.message
    || 'Your account is waiting for admin approval. We will email you when access is granted.';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateRequired, setUpdateRequired] = useState(null);
  // M004-F0626: a boot that could not CONFIRM the session (429/5xx/network) is not
  // a logout. The token stays; these two carry the visible explanation instead.
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  const normalizeUser = useCallback((userData) => {
    if (!userData) {
      return null;
    }

    const email = userData.email || userData.primaryEmail || '';

    return {
      ...userData,
      email,
      primaryEmail: userData.primaryEmail || email,
    };
  }, []);

  const handleAuthError = useCallback((err, fallbackMessage) => {
    const responseBody = err.response?.data || {};
    const structured = responseBody.error && typeof responseBody.error === 'object'
      ? responseBody.error
      : null;
    const details = responseBody.details && typeof responseBody.details === 'object'
      ? responseBody.details
      : {};
    const code = structured?.code
      || responseBody.code
      || responseBody.errorCode
      || (typeof responseBody.error === 'string' ? responseBody.error : '');
    const message = structured?.message || responseBody.message || fallbackMessage;
    setError(message);
    const wrapped = new Error(message);
    if (code) {
      wrapped.code = code;
    }
    if (isPendingApprovalPayload({
      ...responseBody,
      ...(structured || {}),
      code,
    })) {
      wrapped.pendingApproval = true;
    }
    // Pass through structured OAuth hints so callers can branch on
    // LOGIN_METHOD_NOT_LINKED per M001-D002 §12.5.6.
    const oauthHints = {
      ...(structured || {}),
      ...details,
    };
    if (oauthHints.provider) wrapped.provider = oauthHints.provider;
    if (oauthHints.providerEmail || oauthHints.provider_email) {
      wrapped.providerEmail = oauthHints.providerEmail || oauthHints.provider_email;
    }
    const emailAccountExists = oauthHints.emailAccountExists ?? oauthHints.email_account_exists;
    if (typeof emailAccountExists === 'boolean') {
      wrapped.emailAccountExists = emailAccountExists;
    }
    wrapped.status = err.response?.status;
    throw wrapped;
  }, []);

  const applyAuthPayload = useCallback((payload) => {
    const resolvedPayload = payload?.data || payload || {};
    const nextUser = normalizeUser(resolvedPayload.user);

    if (!nextUser) {
      throw new Error('Authentication response did not include a user profile.');
    }

    if (resolvedPayload.token) {
      setAccessToken(resolvedPayload.token);
    }

    setUser(nextUser);
    setUpdateRequired(resolvedPayload.updateRequired || null);
    setError(null);

    return nextUser;
  }, [normalizeUser]);

  // M004-F0626 / WEB-SESSION-001..003: confirm the stored session on mount, and
  // distinguish "this credential was rejected" from "we could not ask". Only the
  // former may clear the token. See src/context/sessionBootstrap.js for why, and
  // for the pipeline evidence that this used to log people out on a 429.
  const bootstrapSession = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setSessionUnavailable(false);
      setSessionError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let lastError = null;

    for (let attempt = 1; attempt <= SESSION_BOOTSTRAP_MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await userApi.getProfile();
        // API returns { success: true, data: { user: {...} } }
        const userData = response.data?.data?.user || response.data?.user || response.data;
        if (userData && userData.role) {
          setUser(normalizeUser(userData));
          setSessionUnavailable(false);
          setSessionError(null);
          setLoading(false);
          return;
        }
        // A 200 that does not describe a user is a genuine identity failure, not a
        // transport one: the server answered and the answer was not an actor.
        console.error('Invalid user data from profile:', response.data);
        clearAccessToken();
        setSessionUnavailable(false);
        setSessionError(null);
        setLoading(false);
        return;
      } catch (err) {
        lastError = err;

        if (isSessionRejected(err)) {
          // The credential itself was rejected. This is the ONLY path that discards it.
          clearAccessToken();
          setUser(null);
          setSessionUnavailable(false);
          setSessionError(null);
          setLoading(false);
          return;
        }

        if (!isTransientSessionFailure(err)) {
          // Unclassifiable. Keep the token — see sessionBootstrap.js on why the two
          // mistakes do not cost the same — and surface it rather than guessing.
          break;
        }

        if (attempt < SESSION_BOOTSTRAP_MAX_ATTEMPTS) {
          const waitMs = sessionBootstrapBackoffMs(err, attempt);
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => { setTimeout(resolve, waitMs); });
        }
      }
    }

    // Retry budget spent on a failure that never said the token was bad. Keep the
    // token, keep the user unauthenticated for now, and say so out loud — a person
    // silently returned to a login form cannot report what went wrong, which is
    // exactly why this defect survived as long as it did.
    console.error('Auth init could not confirm the session (token retained):', lastError);
    setSessionUnavailable(true);
    setSessionError(sessionUnavailableMessage(lastError));
    setLoading(false);
  }, [normalizeUser]);

  // Check if user is authenticated on mount
  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const retrySession = useCallback(() => bootstrapSession(), [bootstrapSession]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login(email, password);
      return applyAuthPayload(response.data);
    } catch (err) {
      return handleAuthError(err, 'Login failed');
    }
  }, [applyAuthPayload, handleAuthError]);

  const loginWithGoogle = useCallback(async (payload) => {
    setError(null);
    try {
      const response = await authApi.loginWithGoogle(payload);
      const authPayload = response.data?.data || response.data || {};
      if (isPendingApprovalPayload(authPayload) && !authPayload.token) {
        setUpdateRequired(null);
        setError(null);
        return {
          pendingApproval: true,
          message: getPendingApprovalMessage(authPayload),
          user: normalizeUser(authPayload.user),
        };
      }
      return applyAuthPayload(authPayload);
    } catch (err) {
      return handleAuthError(err, 'Google sign-in failed');
    }
  }, [applyAuthPayload, handleAuthError, normalizeUser]);

  const loginWithApple = useCallback(async (payload) => {
    setError(null);
    try {
      const response = await authApi.loginWithApple(payload);
      const authPayload = response.data?.data || response.data || {};
      if (isPendingApprovalPayload(authPayload) && !authPayload.token) {
        setUpdateRequired(null);
        setError(null);
        return {
          pendingApproval: true,
          message: getPendingApprovalMessage(authPayload),
          user: normalizeUser(authPayload.user),
        };
      }
      return applyAuthPayload(authPayload);
    } catch (err) {
      return handleAuthError(err, 'Apple sign-in failed');
    }
  }, [applyAuthPayload, handleAuthError, normalizeUser]);

  const requestMagicLink = useCallback(async (email, redirectTo = '/login') => {
    setError(null);
    try {
      const response = await authApi.requestMagicLink(email, redirectTo);
      return response.data?.data || response.data || {};
    } catch (err) {
      return handleAuthError(err, 'Magic-link request failed');
    }
  }, [handleAuthError]);

  const verifyMagicLinkToken = useCallback(async (token) => {
    setError(null);
    try {
      const response = await authApi.verifyMagicLink(token);
      const authPayload = response.data?.data || response.data || {};
      const userData = applyAuthPayload(authPayload);
      const redirectTo = authPayload.redirectTo || authPayload.redirect_to || '';
      return redirectTo ? { ...userData, redirectTo } : userData;
    } catch (err) {
      return handleAuthError(err, 'Magic-link verification failed');
    }
  }, [applyAuthPayload, handleAuthError]);

  const refreshLoginMethods = useCallback(async () => {
    const response = await authApi.getLoginMethods();
    return response.data?.data?.loginMethods || response.data?.loginMethods || response.data?.data || [];
  }, []);

  const linkGoogleLoginMethod = useCallback(async (payload) => {
    const response = await authApi.linkGoogle(payload);
    return response.data?.data || response.data || {};
  }, []);

  const linkAppleLoginMethod = useCallback(async (payload) => {
    const response = await authApi.linkApple(payload);
    return response.data?.data || response.data || {};
  }, []);

  const unlinkLoginMethod = useCallback(async (provider) => {
    const response = await authApi.unlinkLoginMethod(provider);
    return response.data?.data || response.data || {};
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authApi.register(data);
      const payload = response.data?.data || response.data || {};

      if (payload.token && payload.user) {
        return applyAuthPayload(payload);
      }

      setUpdateRequired(null);
      setError(null);

      return {
        pendingApproval: true,
        message: getPendingApprovalMessage(payload),
        user: normalizeUser(payload.user),
      };
    } catch (err) {
      return handleAuthError(err, 'Registration failed');
    }
  }, [applyAuthPayload, handleAuthError, normalizeUser]);

  const logout = useCallback(async () => {
    const outgoingUserId = user?.id;
    try {
      await disableWebPush();
    } catch (err) {
      console.error('Failed to disable web push during logout:', err);
    }

    try {
      await authApi.logout();
    } catch (err) {
      // Ignore logout errors
    }
    await revokeChoreographyAuthorization().catch(() => undefined);
    clearAccessToken();
    // M004-F0613 / T-M004-0020 §6: the in-memory dialog draft store must be cleared
    // synchronously as part of logout, before any subsequent login completes — this app's
    // logout is a SPA state transition (no full page reload), so nothing else clears it.
    clearAllDrafts();
    purgeRehearsalSnapshots(outgoingUserId);
    await purgeCollaborationQueues(outgoingUserId).catch((err) => console.error('Failed to purge collaboration recovery data:', err));
    setUser(null);
    setUpdateRequired(null);
  }, [user?.id]);

  const updateUser = useCallback((userData) => {
    setUser((prev) => normalizeUser({ ...prev, ...userData }));
  }, [normalizeUser]);

  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  const hasPermission = useCallback((permission) => {
    return user?.permissions?.includes(permission) || false;
  }, [user]);

  // Check for admin or super_admin role
  const isAdmin = useCallback(() => hasRole('admin') || hasRole('super_admin'), [hasRole]);
  const isTeacher = useCallback(() => hasRole('teacher'), [hasRole]);
  const isStudent = useCallback(() => hasRole('student') || hasRole('parent'), [hasRole]);

  const dismissUpdateBanner = useCallback(() => {
    setUpdateRequired(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    sessionUnavailable,
    sessionError,
    retrySession,
    isAuthenticated: !!user,
    login,
    loginWithGoogle,
    loginWithApple,
    register,
    logout,
    requestMagicLink,
    verifyMagicLinkToken,
    refreshLoginMethods,
    linkGoogleLoginMethod,
    linkAppleLoginMethod,
    unlinkLoginMethod,
    updateUser,
    hasRole,
    hasPermission,
    isAdmin,
    updateRequired,
    dismissUpdateBanner,
    isTeacher,
    isStudent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
